/**
 * CompetitionTab — IN REVIEW stage
 * Competitor cards with comparison fields, add/remove
 */

import { useState } from 'react'
import type { ProjectDetailProject, Competitor } from '../../../types'
import { CompletableSection } from '../../ui/CompletableSection'
import type { SectionCompletionState } from '../../../hooks/useSectionCompletions'

export interface TabContentProps {
  project: ProjectDetailProject
  projectId: number
  domain: string
  onFieldChange: (field: string, value: any) => void
  sectionCompletions?: SectionCompletionState
  tabId?: string
}

export function CompetitionTab({
  project,
  projectId,
  domain,
  onFieldChange,
  sectionCompletions,
  tabId,
}: TabContentProps) {
  const sc = sectionCompletions
  const isComplete = (sectionId: string) => sc?.isSectionComplete(tabId || '', sectionId) ?? false
  const getInfo = (sectionId: string) => sc?.getCompletionInfo(tabId || '', sectionId)
  const toggle = (sectionId: string) => sc?.toggleSection(tabId || '', sectionId)

  const [competitors, setCompetitors] = useState<
    Competitor[]
  >(project.competitors || [])

  const [newCompetitor, setNewCompetitor] = useState({
    name: '',
    stage: '',
    approach: '',
  })

  const handleAddCompetitor = () => {
    if (newCompetitor.name.trim()) {
      const updated = [...competitors, {
        id: Date.now().toString(),
        ...newCompetitor,
      } as Competitor]
      setCompetitors(updated)
      onFieldChange('competitors', updated)
      setNewCompetitor({ name: '', stage: '', approach: '' })
    }
  }

  const handleRemoveCompetitor = (competitorId: string | number) => {
    const updated = competitors.filter((c) => c.id !== competitorId)
    setCompetitors(updated)
    onFieldChange('competitors', updated)
  }

  return (
    <div className="space-y-6">
      {/* Existing Competitors */}
      <div data-section-id="comp-competitors">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-[13px] font-medium text-[#e8e8f0]">
            Competitors
          </h3>
        </div>
        <div className="space-y-4">
          {competitors.length > 0 ? (
            competitors.map((competitor) => (
              <div
                key={competitor.id}
                className="bg-[#1e1e38] border border-[#2a2a44] rounded-xl p-5"
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h4 className="text-[14px] font-semibold text-[#e8e8f0]">
                      {competitor.name}
                    </h4>
                    <p className="text-[12px] text-[#a8a8c0] mt-1">
                      {competitor.stage}
                    </p>
                  </div>
                  <button
                    onClick={() => handleRemoveCompetitor(competitor.id)}
                    className="
                      text-[12px] text-[#E61B25]
                      hover:text-[#d41620]
                      font-medium
                    "
                  >
                    Remove
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] font-medium text-[#a8a8c0] block mb-1">
                      Development Stage
                    </label>
                    <div className="text-[13px] text-[#e8e8f0]">
                      {competitor.dev_stage || 'N/A'}
                    </div>
                  </div>
                  <div>
                    <label className="text-[11px] font-medium text-[#a8a8c0] block mb-1">
                      Approach
                    </label>
                    <div className="text-[13px] text-[#e8e8f0]">
                      {competitor.approach || 'N/A'}
                    </div>
                  </div>
                  <div>
                    <label className="text-[11px] font-medium text-[#a8a8c0] block mb-1">
                      Key Advantage
                    </label>
                    <div className="text-[13px] text-[#e8e8f0]">
                      {competitor.advantage || 'Not assessed'}
                    </div>
                  </div>
                  <div>
                    <label className="text-[11px] font-medium text-[#a8a8c0] block mb-1">
                      Threat Level
                    </label>
                    <div className={`text-[13px] font-medium ${
                      competitor.threat_level === 'High' ? 'text-[#E61B25]' :
                      competitor.threat_level === 'Medium' ? 'text-[#F59E0B]' :
                      'text-[#10B981]'
                    }`}>
                      {competitor.threat_level || 'Low'}
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="
              bg-[#1e1e38] border border-[#2a2a44]
              rounded-xl p-5
              text-center text-[13px] text-[#a8a8c0]
            ">
              No competitors added yet
            </div>
          )}
        </div>
      </div>

      {/* Add Competitor */}
      <div className="bg-[#1e1e38] border border-[#2a2a44] rounded-xl p-5">
        <h3 className="text-[13px] font-medium text-[#e8e8f0] mb-4">
          Add Competitor
        </h3>
        <div className="space-y-3">
          <input
            type="text"
            placeholder="Competitor company name"
            value={newCompetitor.name}
            onChange={(e) =>
              setNewCompetitor({ ...newCompetitor, name: e.target.value })
            }
            className="
              w-full px-3 py-2
              bg-[#12122a] border border-[#2a2a44]
              rounded-lg
              text-[#e8e8f0] text-[13px]
              placeholder:text-[#5a5a6c]
              focus:border-[#3a3a54]
            "
          />
          <input
            type="text"
            placeholder="Development stage"
            value={newCompetitor.stage}
            onChange={(e) =>
              setNewCompetitor({ ...newCompetitor, stage: e.target.value })
            }
            className="
              w-full px-3 py-2
              bg-[#12122a] border border-[#2a2a44]
              rounded-lg
              text-[#e8e8f0] text-[13px]
              placeholder:text-[#5a5a6c]
              focus:border-[#3a3a54]
            "
          />
          <input
            type="text"
            placeholder="Technical approach"
            value={newCompetitor.approach}
            onChange={(e) =>
              setNewCompetitor({ ...newCompetitor, approach: e.target.value })
            }
            className="
              w-full px-3 py-2
              bg-[#12122a] border border-[#2a2a44]
              rounded-lg
              text-[#e8e8f0] text-[13px]
              placeholder:text-[#5a5a6c]
              focus:border-[#3a3a54]
            "
          />
          <button
            onClick={handleAddCompetitor}
            className="
              w-full px-4 py-2
              bg-[#12122a] border border-dashed border-[#2a2a44]
              rounded-lg
              text-[13px] font-medium text-[#a8a8c0]
              hover:border-[#3a3a54] hover:text-[#e8e8f0]
              transition-colors
            "
          >
            + Add Competitor
          </button>
        </div>
      </div>

      {/* Competitive Landscape Summary */}
      <CompletableSection
        sectionId="comp-landscape-summary"
        title="Competitive Landscape Summary"
        isComplete={isComplete('comp-landscape-summary')}
        completedByName={getInfo('comp-landscape-summary')?.completed_by_name}
        completedAt={getInfo('comp-landscape-summary')?.completed_at}
        onToggle={() => toggle('comp-landscape-summary')}
      >
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-[#12122a] border border-[#2a2a44] rounded-lg p-3">
            <div className="text-[12px] text-[#a8a8c0]">Total Competitors</div>
            <div className="text-[15px] font-medium text-[#e8e8f0] mt-1">
              {competitors.length}
            </div>
          </div>
          <div className="bg-[#12122a] border border-[#2a2a44] rounded-lg p-3">
            <div className="text-[12px] text-[#a8a8c0]">High Threat</div>
            <div className="text-[15px] font-medium text-[#E61B25] mt-1">
              {competitors.filter((c) => c.threat_level === 'High').length}
            </div>
          </div>
          <div className="bg-[#12122a] border border-[#2a2a44] rounded-lg p-3">
            <div className="text-[12px] text-[#a8a8c0]">Competitive Position</div>
            <div className="text-[13px] text-[#e8e8f0] mt-1">
              Moderate
            </div>
          </div>
        </div>
      </CompletableSection>
    </div>
  )
}

export default CompetitionTab
